import type { LucideIcon } from 'lucide-react'
import type { PathLink } from '@/hooks/usePathHelper'
import type { Location, LocationId } from '@/lib/services/locations/locations.types'
import type {
  LocationFulfillmentSetupLocation,
  LocationFulfillmentStatus,
} from './LocationFulfillmentSetupActions'

import { AdminHeaderActionButton } from '@/components/patterns/admin-page'
import { RecordHistoryActionMenu } from '@/components/audit-log/RecordHistoryMenu'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { cn } from '@/lib/utils'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Calendar,
  CheckCircle2,
  CircleOff,
  MapPin,
  ShoppingBasket,
  TriangleAlert,
  Truck,
} from 'lucide-react'
import { getLocationFulfillmentSetupState } from './LocationFulfillmentSetupActions'

const statusConfig = {
  ready: {
    label: 'On',
    icon: CheckCircle2,
    className:
      'border-status-success-border bg-status-success-background text-status-success-foreground',
  },
  needs_setup: {
    label: 'Needs setup',
    icon: TriangleAlert,
    className:
      'border-status-warning-border bg-status-warning-background text-status-warning-foreground',
  },
  off: {
    label: 'Off',
    icon: CircleOff,
    className:
      'border-border/50 bg-muted/70 text-muted-foreground dark:border-white/10 dark:bg-white/[0.06]',
  },
}

const normalizePath = (path: string = '') => String(path).replace(/\/$/, '')

function getCurrentLocationSection(pathname: string, locationId: LocationId) {
  const currentPath = normalizePath(pathname)
  const locationPath = `/settings/locations/${locationId}`

  if (currentPath.endsWith(`${locationPath}/hours`)) return 'pickup'
  if (currentPath.endsWith(`${locationPath}/deliveries`)) return 'delivery'
  if (currentPath.endsWith(locationPath)) return 'location'

  return null
}

function StatusPill({ status }: { status?: LocationFulfillmentStatus | null }) {
  if (!status) return null

  const config = statusConfig[status]
  const Icon = config.icon

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[0.6875rem] leading-4 font-semibold whitespace-nowrap',
        config.className
      )}
    >
      <Icon className="size-3" />
      {config.label}
    </span>
  )
}

function HeaderAction({
  icon: Icon,
  isCurrent = false,
  label,
  link,
  status = null,
}: {
  icon: LucideIcon
  isCurrent?: boolean
  label: string
  link: PathLink
  status?: LocationFulfillmentStatus | null
}) {
  const accessibleLabel = status ? `${label} ${statusConfig[status].label}` : label

  return (
    <AdminHeaderActionButton
      asChild
      variant="outline"
      className={cn(
        'h-10 justify-between gap-2 rounded-full border-border/70 bg-background/75 px-3 text-foreground shadow-xs hover:border-primary/25 hover:bg-muted/65 dark:bg-background/20 dark:hover:bg-background/35',
        status && 'min-w-[8.75rem] pr-2',
        isCurrent && 'border-primary/35 bg-primary/10 text-interaction hover:bg-primary/12'
      )}
    >
      <Link
        {...link}
        activeOptions={{ exact: true }}
        aria-current={isCurrent ? 'page' : undefined}
        aria-label={accessibleLabel}
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <Icon className="size-4" />
          <span className="truncate">{label}</span>
        </span>
        <StatusPill status={status} />
      </Link>
    </AdminHeaderActionButton>
  )
}

export function LocationSettingsActionGroup({
  className,
  location,
  locationId,
}: {
  className?: string
  location?: (LocationFulfillmentSetupLocation & Pick<Location, 'id'>) | null
  locationId?: LocationId | null
}) {
  const resolvedLocationId = locationId ?? location?.id
  const path = usePathHelper()
  const router = useRouterState()

  if (!resolvedLocationId) return null

  const currentSection = getCurrentLocationSection(router.location.pathname, resolvedLocationId)
  const fulfillmentState = location ? getLocationFulfillmentSetupState(location) : null
  const locationLink = path.locations.show(resolvedLocationId).link
  const historyLink = {
    ...locationLink,
    to: `${locationLink.to.replace(/\/$/, '')}/history`,
  }
  const pickupLink = path.hours.index().using({ locationId: resolvedLocationId }).link
  const deliveryLink = path.deliveries.index().using({ locationId: resolvedLocationId }).link
  const availabilityLink = path.availability?.index?.()?.link

  return (
    <div className={cn('flex flex-wrap items-center justify-end gap-2', className)}>
      <HeaderAction
        icon={MapPin}
        isCurrent={currentSection === 'location'}
        label="Location"
        link={locationLink}
      />
      <Can do="edit" on="hours">
        <HeaderAction
          icon={ShoppingBasket}
          isCurrent={currentSection === 'pickup'}
          label="Pickup"
          link={pickupLink}
          status={fulfillmentState?.pickup.status}
        />
      </Can>
      <Can do="edit" on="delivery">
        <HeaderAction
          icon={Truck}
          isCurrent={currentSection === 'delivery'}
          label="Delivery"
          link={deliveryLink}
          status={fulfillmentState?.delivery.status}
        />
      </Can>
      {availabilityLink ? (
        <Can do="list" on="availability-changes">
          <HeaderAction icon={Calendar} label="Availability" link={availabilityLink} />
        </Can>
      ) : null}
      <RecordHistoryActionMenu link={historyLink} triggerLabel="More location actions" />
    </div>
  )
}
